import React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'

import * as modalAction from '../../../actions/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import useDispatch from '../../../hooks/use-dispatch'
import useSelector from '../../../hooks/use-selector'
import * as priceApi from '../../../services/api/price'
import {formDataToPartialPriceRequest, pricingsToFormData} from '../../forms/pricings/utils'
import {toMixedValidationSchema, toTouchedFormData} from '../../utils/form'

import {ModalMeta, Price} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import PricingsForm, {
  MixedFormData,
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../forms/pricings/form'

type Props = {
  meta: ModalMeta
  pricings: Price[]
  onDone?: () => void
}

const EditPricingsModal = ({meta, pricings = [], onDone = noop}: Props) => {
  const dispatch = useDispatch()
  const provider = useSelector(state => state.core.provider)
  invariant(provider, 'Provider missing.')

  const editMultiple = pricings.length > 1

  const formik = useFormik<MixedFormData>({
    initialValues: {
      ...initialValues,
      ...pricingsToFormData(pricings),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const priceRequest = formDataToPartialPriceRequest(touchedValues)

      try {
        const pricingIds = pricings.map(({id}) => id)
        await priceApi.patchMultiple(priceRequest, pricingIds)

        onDone()
        dispatch(modalAction.close())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={
        <Headline
          label="Edit pricing"
          addendum={editMultiple && `(${pricings.length} items)`}
          size="l"
        />
      }
      onClose={() => dispatch(modalAction.close())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(modalAction.close())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <PricingsForm formik={formik} provider={provider} />
    </Modal>
  )
}

export default EditPricingsModal
